import categories from "@/constants/categories.js";
import sectionsEnum from "@/constants/sections.enum.js";

const root = { root: true };

const CALCULATORS_CATEGORY = 7;
const DRUG_CATEGORIES = [1, 2, 4, 5];

/**
 * Tópicos que existem no enum de seções mas cujos itens são, na verdade,
 * itens de categoria legada (produtos). Espelha a regra de `Items.vue#openSection`.
 */
const LEGACY_CATEGORY_TOPICS = [
    "supplements",
    "foods",
    "oral_hygiene",
    "suplementos",
    "racoes",
    "higiene oral",
];

const normalize = (value) => {
    if (!value && value !== 0) return "";
    return `${value}`
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim();
};

const isLegacyCategoryTopic = (topic) => {
    return LEGACY_CATEGORY_TOPICS.includes(normalize(topic));
};

const getCategoryId = (item) => {
    const category = parseInt(
        item && item.category && item.category.id !== undefined
            ? item.category.id
            : (item || {}).category
    );
    return isNaN(category) ? null : category;
};

/**
 * Encontra a seção do catálogo correspondente ao tópico do item, aceitando
 * tanto o id (`diseases`) quanto o nome exibido (`Doenças`).
 */
const findSectionByTopic = (topic) => {
    const target = normalize(topic);
    if (!target) return null;
    return (
        sectionsEnum.sections.find((s) => normalize(s.id) === target) ||
        sectionsEnum.sections.find((s) => normalize(s.name) === target) ||
        sectionsEnum.sections.find((s) =>
            (s.contentTopics || []).some((t) => normalize(t) === target)
        ) ||
        null
    );
};

const isTopicContent = (item) => {
    return !!item && !!item.topic && !isLegacyCategoryTopic(item.topic);
};

/**
 * Tipo lógico do conteúdo, usado nos eventos de analytics.
 * @returns {'calculator'|'item'|'product'|'drug'|null}
 */
const getContentKind = (item) => {
    if (!item) return null;
    const category = getCategoryId(item);
    if (category === CALCULATORS_CATEGORY) return "calculator";
    if (isTopicContent(item)) return "item";
    if (item.type) return "product";
    if (category) return "drug";
    return null;
};

/**
 * Resolve a rota de um conteúdo (favorito, recente ou recomendado).
 * Mesma resolução usada por `Items.vue` no modo favoritos.
 * @returns {string|null} caminho da rota ou null quando não resolvível
 */
const resolveContentPath = (item) => {
    if (!item || !item.id || !item.name) return null;
    const category = getCategoryId(item);
    const kind = getContentKind(item);
    switch (kind) {
        case "calculator":
            return `/calculators/${item.id}`;
        case "item":
            return `/item/${item.id}`;
        case "product":
            return category ? `/categories/${category}/products/${item.id}` : null;
        case "drug":
            return `/categories/${category}/drugs/${item.id}`;
        default:
            return null;
    }
};

/**
 * Abre um conteúdo reaproveitando os fluxos existentes.
 * Calculadoras passam por `openCalculatorById` para respeitar rotas customizadas.
 * @returns {boolean} false quando a rota não pôde ser resolvida
 */
const openContentItem = ({ item, dispatch }) => {
    const path = resolveContentPath(item);
    if (!path) return false;
    if (getContentKind(item) === "calculator") {
        dispatch("calculators/openCalculatorById", { id: item.id }, root);
        return true;
    }
    dispatch("general/routePathRedirect", { path }, root);
    return true;
};

/**
 * Rótulo curto do tipo do conteúdo, exibido acima do nome nos cards da Home.
 */
const CATEGORY_LABELS = {
    3: "Higiene oral",
    6: "Suplemento",
    8: "Ração",
    9: "Ração",
    10: "Ração",
};

const getContentTypeLabel = (item) => {
    if (!item) return "";
    const category = getCategoryId(item);
    if (category === CALCULATORS_CATEGORY) return "Calculadora";
    if (isTopicContent(item)) {
        const section = findSectionByTopic(item.topic);
        return section ? section.shortName || section.name : item.topic;
    }
    if (CATEGORY_LABELS[category]) return CATEGORY_LABELS[category];
    if (DRUG_CATEGORIES.includes(category)) {
        return item.type ? "Produto" : "Medicamento";
    }
    if (item.type) return "Produto";
    if (category && categories[category - 1]) {
        return categories[category - 1].ptShortName || categories[category - 1].portugueseName;
    }
    return "";
};

/**
 * Ícone (chave do registro Vuetify) correspondente ao tipo do conteúdo.
 */
const getContentIcon = (item) => {
    if (!item) return "item";
    const category = getCategoryId(item);
    if (category === CALCULATORS_CATEGORY) return "calculators";
    if (isTopicContent(item)) {
        const section = findSectionByTopic(item.topic);
        return section && section.icon ? section.icon : "item";
    }
    if (category === 2) return "vaccines";
    if (DRUG_CATEGORIES.includes(category)) return "drugs";
    if (category === 6) return "supplements";
    if (category === 3) return "oralHygiene";
    if ([8, 9, 10].includes(category)) return "foods";
    return "item";
};

/**
 * Um conteúdo é PRO quando a seção do seu tópico é PRO.
 * Medicamentos, produtos e calculadoras nunca são PRO.
 */
const isProContent = (item) => {
    if (!item || !isTopicContent(item)) return false;
    const section = findSectionByTopic(item.topic);
    return !!(section && section.pro);
};

/**
 * Nome para exibição. Alguns nomes legados carregam tags HTML
 * (ex.: "<u><b>C2</b></u>"); saem só na tela, o dado fica intacto até a
 * migração do banco.
 */
const getDisplayName = (item) => {
    const raw = item && item.name ? `${item.name}` : "";
    return raw
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .replace(/\s+/g, " ")
        .trim();
};

/**
 * Chave estável de deduplicação: tipo + categoria/tópico + id.
 */
const getContentKey = (item) => {
    if (!item || !item.id) return null;
    const scope = isTopicContent(item)
        ? normalize(item.topic)
        : `${getCategoryId(item) || ""}`;
    return `${getContentKind(item) || "unknown"}|${scope}|${normalize(item.id)}`;
};

const isSameContent = (a, b) => {
    const keyA = getContentKey(a);
    return !!keyA && keyA === getContentKey(b);
};

export default {
    getContentIcon,
    getContentKey,
    getContentKind,
    getContentTypeLabel,
    getDisplayName,
    isProContent,
    isSameContent,
    openContentItem,
    resolveContentPath,
};
